import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import { SentryService } from './sentryService';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
/** A backend error code: SCREAMING_SNAKE_CASE, safe to use as a translation key. */
const CODE_SHAPE = /^[A-Z][A-Z0-9_]*$/;

/**
 * The code from an error body. The API has two shapes: `errorCode` plus English in `error`, or a
 * code in `error` plus English in `message`. The shape check is what stops an English sentence
 * being mistaken for a code and looked up as a translation key.
 */
export const resolveErrorCode = (
  errorCode: unknown,
  rawError: string,
): string | undefined => {
  if (typeof errorCode === 'string' && CODE_SHAPE.test(errorCode))
    return errorCode;
  return CODE_SHAPE.test(rawError) ? rawError : undefined;
};

export class ApiError extends Error {
  public status: number;
  public data: unknown;
  public isNetworkError: boolean;
  /** Server error code (e.g. WALLET_FROZEN, TRANSACTION_ALREADY_PROCESSED) for use in catch blocks */
  public errorCode: string | undefined;
  /** Values for the code's `{{placeholders}}`, so a translated message can be interpolated. */
  public errorParams: Record<string, string | number> | undefined;
  /** Seconds to wait before retrying. Populated for 429 / 503 responses that include Retry-After. */
  public retryAfterSeconds: number | undefined;

  constructor(
    message: string,
    status: number,
    data?: unknown,
    isNetworkError = false,
    errorCode?: string,
    retryAfterSeconds?: number,
    errorParams?: Record<string, string | number>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.isNetworkError = isNetworkError;
    this.errorCode = errorCode;
    this.retryAfterSeconds = retryAfterSeconds;
    this.errorParams = errorParams;
  }
}

/**
 * Parses the `Retry-After` HTTP header — RFC 7231 allows either delta-seconds
 * (an integer) or an HTTP-date. Returns seconds-from-now, or undefined if the
 * header is missing/unparseable. Used to surface a wait hint on 429/503s.
 */
function parseRetryAfter(headerValue: unknown): number | undefined {
  if (headerValue == null) return undefined;
  const raw = String(headerValue).trim();
  if (!raw) return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds);
  const asDate = Date.parse(raw);
  if (!Number.isNaN(asDate)) {
    const delta = Math.round((asDate - Date.now()) / 1000);
    return delta > 0 ? delta : 0;
  }
  return undefined;
}

export class ApiService {
  private static instance: ApiService;
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }> = [];

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Handle 401 unauthorized errors - logout user and trigger redirect
   */
  private static handle401Error(): void {
    // Best-effort server-side logout (send refresh token for revocation)
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      fetch(`${API_BASE_URL}/public/api/v1/auth/signOut`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {
        /* best-effort */
      });
    }
    authService.signOut();
    window.dispatchEvent(new CustomEvent('auth:logout-required'));
  }

  /**
   * Process queued requests after token refresh
   */
  private processQueue(error: unknown, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else if (token) {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /**
   * Setup request and response interceptors for global handling
   */
  private setupInterceptors(): void {
    // Request interceptor - add auth token and track timing
    this.axiosInstance.interceptors.request.use(
      config => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = token;
        }

        // Track request start time for performance monitoring
        (config as unknown as Record<string, unknown>).metadata = {
          startTime: Date.now(),
        };

        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    // Response interceptor - handle common errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Track successful API calls
        SentryService.trackPerformance(
          `${response.config.method?.toUpperCase()} ${response.config.url}`,
          Date.now() -
            ((
              (response.config as unknown as Record<string, unknown>)
                .metadata as { startTime?: number }
            )?.startTime || Date.now()),
          {
            status: response.status,
            endpoint: response.config.url,
            method: response.config.method,
          },
        );

        // Don't automatically convert response body errors to ApiError
        // Let developers decide how to handle them
        return response;
      },
      async (error: AxiosError) => {
        if (error.response) {
          // Server responded with error status
          const { status, data } = error.response;
          const endpoint = error.config?.url || 'unknown';
          const method = error.config?.method || 'unknown';

          // Handle 401 errors — attempt token refresh before logging out
          if (status === 401) {
            const originalRequest = error.config as
              (typeof error.config & { _retry?: boolean }) | undefined;

            // Hard break — log out instead of looping when either:
            //  - the refresh endpoint itself returned 401, or
            //  - this request was already retried once with a fresh token and
            //    STILL got 401 (the token is permanently invalid — e.g. a
            //    deleted / blacklisted account). Refreshing again can't help.
            if (
              originalRequest?.url?.includes('/auth/refresh') ||
              originalRequest?._retry
            ) {
              ApiService.handle401Error();
              return Promise.reject(new ApiError('Unauthorized', status, data));
            }

            // If already refreshing, queue this request
            if (this.isRefreshing) {
              return new Promise((resolve, reject) => {
                this.failedQueue.push({
                  resolve: (token: string) => {
                    if (originalRequest) {
                      // Single-shot: if this retry still 401s, the hard-break
                      // above logs out instead of triggering another refresh.
                      originalRequest._retry = true;
                      originalRequest.headers.Authorization = token;
                      resolve(this.axiosInstance(originalRequest));
                    }
                  },
                  reject,
                });
              });
            }

            this.isRefreshing = true;

            try {
              const newTokens = await authService.refreshTokens();
              if (newTokens) {
                this.processQueue(null, newTokens.accessToken);
                // Retry the original request with new token
                if (originalRequest) {
                  // Single-shot: a second 401 on this request hits the
                  // hard-break above and logs out rather than looping.
                  originalRequest._retry = true;
                  originalRequest.headers.Authorization = newTokens.accessToken;
                  return this.axiosInstance(originalRequest);
                }
              } else {
                this.processQueue(new Error('Refresh failed'));
                ApiService.handle401Error();
              }
            } catch (refreshError) {
              this.processQueue(refreshError);
              ApiService.handle401Error();
              return Promise.reject(new ApiError('Unauthorized', status, data));
            } finally {
              this.isRefreshing = false;
            }
          }

          // Track API errors in Sentry (excluding 401s which are expected)
          SentryService.trackApiError(endpoint, method, error, status);

          let message = 'An error occurred';
          let errorCode: string | undefined;
          let errorParams: Record<string, string | number> | undefined;

          // Extract user-facing message and error code from response body
          // Prefer 'message' (user-facing) over 'error' (often a code) for display
          if (data && typeof data === 'object') {
            const dataObj = data as Record<string, unknown>;
            const rawError =
              typeof dataObj.error === 'string' ? dataObj.error : '';
            errorCode = resolveErrorCode(dataObj.errorCode, rawError);
            errorParams =
              dataObj.errorParams && typeof dataObj.errorParams === 'object'
                ? (dataObj.errorParams as Record<string, string | number>)
                : undefined;
            message =
              (typeof dataObj.message === 'string' && dataObj.message) ||
              rawError ||
              'An error occurred';
          }

          // 429 (and gateway-throttled 503s) get a unified, friendly message
          // regardless of what the upstream returned — API Gateway's native
          // bodies are terse ("Limit Exceeded", "Too Many Requests") and our
          // app-level limiter has a different shape. We override here so the
          // UX is consistent. The Retry-After header (when present) is
          // surfaced on `ApiError.retryAfterSeconds` so callers can show a
          // countdown.
          const retryAfterSeconds = parseRetryAfter(
            error.response.headers?.['retry-after'] ??
              error.response.headers?.['Retry-After'],
          );
          if (status === 429) {
            message =
              retryAfterSeconds != null
                ? `Too many requests. Please try again in ${retryAfterSeconds}s.`
                : 'Too many requests. Please slow down and try again.';
          } else if (message === 'An error occurred') {
            // Fall back to generic HTTP status messages
            switch (status) {
              case 401:
                message = 'Unauthorized - Please log in again';
                break;
              case 403:
                message =
                  "Forbidden - You don't have permission to access this resource";
                break;
              case 404:
                message = 'Resource not found';
                break;
              case 422:
                message = 'Validation error';
                break;
              case 500:
                message = 'Internal server error';
                break;
              case 503:
                message =
                  retryAfterSeconds != null
                    ? `Service temporarily unavailable. Please try again in ${retryAfterSeconds}s.`
                    : 'Service temporarily unavailable. Please try again shortly.';
                break;
              default:
                message = `Request failed with status ${status}`;
            }
          }

          return Promise.reject(
            new ApiError(
              message,
              status,
              data,
              false,
              errorCode,
              retryAfterSeconds,
              errorParams,
            ),
          );
        } else if (error.request) {
          // Network error
          const endpoint = error.config?.url || 'unknown';
          const method = error.config?.method || 'unknown';

          // Track network errors in Sentry
          SentryService.trackApiError(endpoint, method, error, 0);

          return Promise.reject(
            new ApiError(
              'Network error - Please check your connection',
              0,
              null,
              true,
            ),
          );
        } else {
          // Other error
          return Promise.reject(
            new ApiError(error.message || 'An unexpected error occurred', 0),
          );
        }
      },
    );
  }

  /**
   * Make a request with proper error handling
   */
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.request<T>(config);
      return response.data;
    } catch (error) {
      // Re-throw ApiError instances, wrap others
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('An unexpected error occurred', 0);
    }
  }

  /**
   * Make a GET request
   */
  public async get<T>(
    endpoint: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({
      method: 'GET',
      url: endpoint,
      ...config,
    });
  }

  /**
   * Make a POST request
   */
  public async post<T, U = unknown>(
    endpoint: string,
    data?: U,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({
      method: 'POST',
      url: endpoint,
      data,
      ...config,
    });
  }

  /**
   * Make a PUT request
   */
  public async put<T, U = unknown>(
    endpoint: string,
    data?: U,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({
      method: 'PUT',
      url: endpoint,
      data,
      ...config,
    });
  }

  /**
   * Make a DELETE request
   */
  public async delete<T>(
    endpoint: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({
      method: 'DELETE',
      url: endpoint,
      ...config,
    });
  }

  /**
   * Make a PATCH request
   */
  public async patch<T, U = unknown>(
    endpoint: string,
    data?: U,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({
      method: 'PATCH',
      url: endpoint,
      data,
      ...config,
    });
  }

  /**
   * Make a POST request with FormData (for file uploads)
   */
  public async postFormData<T>(
    endpoint: string,
    formData: FormData,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({
      method: 'POST',
      url: endpoint,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config,
    });
  }

  /**
   * Create a cancelable request
   * @returns Object with request promise and cancel function
   */
  public createCancelableRequest<T>(config: AxiosRequestConfig) {
    const controller = new AbortController();
    const request = this.request<T>({
      ...config,
      signal: controller.signal,
    });

    return {
      request,
      cancel: () => controller.abort(),
    };
  }

  /**
   * Update the base URL (useful for switching environments)
   */
  public setBaseURL(url: string): void {
    this.axiosInstance.defaults.baseURL = url;
  }

  /**
   * Update the request timeout
   */
  public setTimeout(timeout: number): void {
    this.axiosInstance.defaults.timeout = timeout;
  }

  /**
   * Check if a response contains an error message in the body
   * Useful for APIs that return success responses with error fields
   */
  public static hasErrorInResponse<T>(response: T): boolean {
    return (
      response &&
      typeof response === 'object' &&
      'error' in response &&
      response.error !== null &&
      response.error !== undefined
    );
  }

  /**
   * Extract user-facing message from response body (prefers message, else error)
   * Returns null if no error is found
   */
  public static getErrorMessage<T>(response: T): string | null {
    if (!response || typeof response !== 'object') return null;
    const obj = response as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message) return obj.message;
    if (this.hasErrorInResponse(response)) {
      const error = obj.error;
      return typeof error === 'string' ? error : 'An error occurred';
    }
    return null;
  }

  /**
   * Extract error code from response body (e.g. WALLET_FROZEN, TRANSACTION_ALREADY_PROCESSED)
   */
  public static getErrorCode<T>(response: T): string | null {
    if (!response || typeof response !== 'object') return null;
    const obj = response as Record<string, unknown>;
    return (
      resolveErrorCode(
        obj.errorCode,
        typeof obj.error === 'string' ? obj.error : '',
      ) ?? null
    );
  }

  /**
   * Create an ApiError from a response that contains an error
   * Useful when you want to treat a response with an error field as an error
   * Note: HTTP status errors (like 401) are handled by the response interceptor
   */
  public static getErrorParams<T>(
    response: T,
  ): Record<string, string | number> | undefined {
    if (!response || typeof response !== 'object') return undefined;
    const params = (response as Record<string, unknown>).errorParams;
    return params && typeof params === 'object'
      ? (params as Record<string, string | number>)
      : undefined;
  }

  public static createErrorFromResponse<T>(
    response: T,
    status: number = 400,
  ): ApiError {
    const message = this.getErrorMessage(response) || 'An error occurred';
    const errorCode = this.getErrorCode(response) ?? undefined;
    return new ApiError(
      message,
      status,
      response,
      false,
      errorCode,
      undefined,
      this.getErrorParams(response),
    );
  }

  /**
   * Make a request and automatically handle response body errors
   * This method will throw an ApiError if the response contains an error field
   * Note: HTTP status errors (like 401) are handled by the response interceptor
   */
  public async requestWithErrorHandling<T>(
    config: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.request<T>(config);

      // Check if response body contains an error
      if (ApiService.hasErrorInResponse(response.data)) {
        const errorMessage =
          ApiService.getErrorMessage(response.data) || 'An error occurred';
        const errorCode = ApiService.getErrorCode(response.data) ?? undefined;
        throw new ApiError(
          errorMessage,
          response.status,
          response.data,
          false,
          errorCode,
        );
      }

      return response.data;
    } catch (error) {
      // Re-throw ApiError instances (already handled by interceptor)
      if (error instanceof ApiError) {
        throw error;
      }
      // Re-throw AxiosError (will be handled by interceptor)
      throw error;
    }
  }
}

export const apiService = ApiService.getInstance();
